import React, { useEffect, useState } from 'react';
import { BroadcastStatus } from '../../../common/enums/BroadcastStatus';
import {
  PROGRESS_BAR_ANIMATION,
  generateStripeGradient,
  generateStripeAnimation,
} from '../utils';

interface ProgressBarProps {
  percentage: number;
  label?: string;
  status?: BroadcastStatus | string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  percentage,
  label,
  status,
}) => {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  // Hide the progress bar if in terminal state (completed, failed, or 100%)
  const isTerminalState =
    status === BroadcastStatus.COMPLETED ||
    status === BroadcastStatus.FAILED ||
    percentage === 100;

  // Hide the progress bar if in terminal state
  if (isTerminalState) {
    return null;
  }

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setAnimatedPercentage(percentage);
    }, 100);

    return () => clearTimeout(timer);
  }, [percentage]);

  const { colors, duration, distance, stripeSize } = PROGRESS_BAR_ANIMATION;

  return (
    <div className="w-full">
      {label && (
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-sm font-semibold text-gray-900">
            {percentage.toFixed(0)}%
          </span>
        </div>
      )}
      <div className="relative h-4 w-full overflow-hidden rounded-full bg-gray-200">
        <style>
          {generateStripeAnimation('slideGrayStripes', distance)}
          {generateStripeAnimation('blueWave', distance)}
        </style>

        {/* Completed part with blue wave animation */}
        <div
          className="absolute left-0 top-0 h-full transition-all duration-1000 ease-out"
          style={{
            width: `${animatedPercentage}%`,
            background: generateStripeGradient(
              colors.completed.light,
              colors.completed.dark,
              stripeSize
            ),
            animation: `blueWave ${duration}s linear infinite`,
          }}
        />

        {/* Remaining part with gray striped animation */}
        <div
          className="absolute right-0 top-0 h-full"
          style={{
            width: `${100 - animatedPercentage}%`,
            background: generateStripeGradient(
              colors.remaining.light,
              colors.remaining.dark,
              stripeSize
            ),
            animation: `slideGrayStripes ${duration}s linear infinite`,
          }}
        />
      </div>
    </div>
  );
};
